'use client'

import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import type { QRCodeToDataURLOptions } from 'qrcode';
import Loader from '@/components/ui/loader';
import Image from 'next/image';

interface TProps {
  value: string;
  size?: number;
  options?: QRCodeToDataURLOptions;
  className?: string;
}

const QRCodeImage = ({
  value,
  size = 256,
  options,
  className,
}: TProps) => {
  const [dataUrl, setDataUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const generateQRCode = async () => {
      if (!value) {
        setDataUrl('');
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        const combinedOptions: QRCodeToDataURLOptions = {
          errorCorrectionLevel: 'H',
          type: 'image/png',
          margin: 1,
          width: size,
          ...options,
        };
        const url = await QRCode.toDataURL(value, combinedOptions);
        setDataUrl(url);
      } catch (err) {
        console.error('Failed to generate QR Code:', err);
        setError('Could not generate QR code.');
        setDataUrl('');
      } finally {
        setIsLoading(false);
      }
    };

    generateQRCode();
  }, [value, size, options]);

  if (error) {    
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`}>
        <span className="text-red-500 text-center p-4">{error}</span>
      </div>
    );
  }
  
  if (isLoading) {    
    return (
      <Loader />
    );
  }
  
  if (!dataUrl) {
    return null;
  }

  return (
    <Image
      src={dataUrl}
      alt={`QR Code for ${value}`}
      width={size}
      height={size}
      className={className}
    />
  );
};

export default QRCodeImage;